/**
 * `/carrinho`.
 *
 * O `409` de versao (`CartVersionConflictError`) **nao** e tratado como erro de tela: o erro ja
 * carrega o carrinho atual e o cofre de `ETag` do `@farmacia/api-client` ja foi atualizado. O
 * que resta e reexibir — que e o que a invalidacao faz.
 *
 * ## A divergencia de filial, e por que a deteccao mora AQUI
 *
 * `DOMAIN` INV-C1: *"uma filial por carrinho"*. O carrinho respeita a regra; o que podia
 * divergir era a filial do CABECALHO — trocada em qualquer tela, sem tocar no carrinho. A
 * comparacao precisa dos dois lados (`GET /v1/cart` e o contexto de "minha loja"), e este e o
 * unico ponto do app em que os dois se encontram. A tela (`telas/Carrinho.tsx`) continua pura:
 * ela recebe a divergencia ja resolvida, com os dois nomes, e desenha.
 *
 * ## O `GET /v1/stores` so acontece quando ha divergencia
 *
 * `CartResponse` carrega `storeId`, nao o nome. Nomear a filial do carrinho custa uma consulta
 * — barata (`public, s-maxage=600`) mas nao gratuita —, e ela fica atras de `enabled`. Sem
 * divergencia, esta tela continua fazendo exatamente uma requisicao, como antes.
 */
import { queryKeys, useApiClient, useApiQueries } from '@farmacia/api-client/query';
import type { CartResponse } from '@farmacia/contracts';
import { useAnnounce } from '@farmacia/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState, type ReactNode } from 'react';

import { texto } from '../conteudo/index.js';
import { useMinhaLoja } from '../loja/contexto.jsx';
import {
  carrinhoDivergeDaFilial,
  corpoDaTrocaDeFilial,
  diferencaDoCarrinho,
  houveMudanca,
} from '../loja/troca-de-filial.js';
import { usePwa } from '../pwa/contexto.jsx';
import { Carrinho, type MudancasDaTroca } from '../telas/Carrinho.jsx';

export const Route = createFileRoute('/carrinho')({ component: TelaDeCarrinho });

function TelaDeCarrinho(): ReactNode {
  const cliente = useApiClient();
  const queries = useApiQueries();
  const queryClient = useQueryClient();
  const navegar = useNavigate();
  const { conexao } = usePwa();
  const { loja, storeId, escolher } = useMinhaLoja();
  const { anunciar } = useAnnounce();

  const consulta = useQuery(queries.cart.current());

  const recarregar = (): void => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.cart.current() });
  };

  const mudarQuantidade = useMutation({
    mutationFn: ({ itemId, quantidade }: { itemId: string; quantidade: number }) =>
      cliente.cart.changeItem(itemId, { quantity: quantidade }),
    onSettled: recarregar,
  });

  const remover = useMutation({
    mutationFn: (itemId: string) => cliente.cart.removeItem(itemId),
    onSettled: recarregar,
  });

  // -------------------------------------------------------------------------
  // Divergencia de filial (INV-C1, RN-076)
  // -------------------------------------------------------------------------

  const diverge = carrinhoDivergeDaFilial(consulta.data, storeId);

  const lojas = useQuery({ ...queries.stores.list(), enabled: diverge });
  const nomeDaLojaDoCarrinho =
    lojas.data?.data.find((filial) => filial.id === consulta.data?.storeId)?.name ?? null;

  /**
   * O que a ultima troca custou.
   *
   * Estado local, e nao derivado do carrinho: depois da troca o carrinho novo **nao sabe** o que
   * ficou para tras — a lista de perdas so existe na comparacao entre a resposta de antes e a de
   * depois, e alguem precisa segura-la ate a pessoa ler.
   */
  const [mudancas, setMudancas] = useState<MudancasDaTroca | undefined>(undefined);

  const levar = useMutation({
    mutationFn: ({ atual, alvo }: { readonly atual: CartResponse; readonly alvo: string }) =>
      cliente.cart.replace(corpoDaTrocaDeFilial(atual, alvo)),
    onSuccess: (novo, { atual }) => {
      const diferenca = diferencaDoCarrinho(atual.items, novo.items);
      queryClient.setQueryData(queryKeys.cart.current(), novo);
      const nomeDaLoja = loja?.nome ?? '';
      if (!houveMudanca(diferenca)) {
        setMudancas(undefined);
        anunciar(texto('loja.troca.semMudanca', { loja: nomeDaLoja }));
        return;
      }
      setMudancas({ diferenca, nomeDaLoja });
      anunciar(
        texto('loja.troca.resumo.anuncio', {
          loja: nomeDaLoja,
          quantidade: diferenca.perdidos.length + diferenca.reduzidos.length,
        }),
      );
    },
    onError: recarregar,
  });

  const carrinhoAtual = consulta.data;
  const divergencia =
    diverge && carrinhoAtual !== undefined && storeId !== null
      ? {
          nomeDaLojaDoCarrinho,
          nomeDaLojaSelecionada: loja?.nome ?? '',
          levando: levar.isPending,
          aoLevarParaLojaSelecionada: () => {
            levar.mutate({ atual: carrinhoAtual, alvo: storeId });
          },
          // Nao chama o servidor: o carrinho ja esta nessa filial. O que muda e so a
          // preferencia do aparelho, e por isso esta saida nao custa item nenhum.
          aoVoltarParaLojaDoCarrinho: () => {
            escolher({ id: carrinhoAtual.storeId, nome: nomeDaLojaDoCarrinho ?? '' });
          },
        }
      : undefined;

  return (
    <Carrinho
      carrinho={consulta.data}
      carregando={consulta.isPending}
      erro={consulta.error}
      aoTentarDeNovo={recarregar}
      offline={conexao === 'OFFLINE'}
      salvando={mudarQuantidade.isPending || remover.isPending}
      aoContinuar={() => void navegar({ to: '/checkout' })}
      aoBuscar={() => void navegar({ to: '/busca' })}
      aoMudarQuantidade={(itemId, quantidade) => { mudarQuantidade.mutate({ itemId, quantidade }); }}
      aoRemover={(itemId) => { remover.mutate(itemId); }}
      {...(divergencia === undefined ? {} : { divergencia })}
      {...(mudancas === undefined ? {} : { mudancas })}
    />
  );
}
